/**
 * Voice AI — Agent Overview Tab
 * Self-loading metrics cards and recent calls list.
 * Fetches data from agent-activity.php (reads SQLite).
 * Dispatches: switch-tab
 */

import { LitElement, html } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { agentStyles } from '../../styles/agent-styles.js';
import { tz } from '../../services/timezone-service.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { agentService } from '../../services/agent-service.js';

export class AgentOverviewTab extends LitElement {
  static properties = {
    agent: { type: Object },
    _loading: { type: Boolean, state: true },
    _stats: { type: Object, state: true },
    _recentCalls: { type: Array, state: true },
    _timelineEvents: { type: Array, state: true },
  };

  static styles = [designSystem, componentStyles, agentStyles];

  constructor() {
    super();
    this._loading = false;
    this._stats = { totalCalls: 0, avgDuration: 0, completedPct: 0 };
    this._recentCalls = [];
    this._timelineEvents = [];
    this._lastAgentId = null;
  }

  updated(changed) {
    if (changed.has('agent') && this.agent?.id !== this._lastAgentId) {
      this._lastAgentId = this.agent?.id;
      this._loadOverview();
    }
  }

  async _loadOverview() {
    if (!this.agent?.id) return;
    this._loading = true;
    try {
      const [callsData, timelineData] = await Promise.all([
        agentService.loadAgentActivity(this.agent.id, { page: 1, limit: 5 }),
        agentService.loadAgentTimeline(this.agent.id, { limit: 5 }),
      ]);
      if (callsData.success) {
        this._stats = callsData.stats || { totalCalls: 0, avgDuration: 0, completedPct: 0 };
        this._recentCalls = callsData.calls || [];
      }
      if (timelineData.success) {
        this._timelineEvents = timelineData.events || [];
      }
    } catch (err) {
      console.error('[OverviewTab] Load failed:', err);
      this._recentCalls = [];
      this._timelineEvents = [];
    } finally {
      this._loading = false;
    }
  }

  _formatDuration(seconds) {
    if (!seconds && seconds !== 0) return '—';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }

  _formatDate(dateStr) {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', { timeZone: tz.tz, month: 'short', day: 'numeric' })
      + ' ' + d.toLocaleTimeString('en-US', { timeZone: tz.tz, hour: 'numeric', minute: '2-digit' });
  }

  _formatPhone(phone) {
    if (!phone) return '—';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  }

  _eventIcon(type) {
    const icons = {
      call_started: 'fa-phone-arrow-down-left',
      call_ended: 'fa-phone-hangup',
      reservation_created: 'fa-calendar-check',
      payment_link_sent: 'fa-credit-card',
      tool_call_completed: 'fa-wrench',
      tool_call_failed: 'fa-circle-exclamation',
    };
    return icons[type] || 'fa-circle-dot';
  }

  _eventText(evt) {
    const d = evt.data || {};
    switch (evt.eventType) {
      case 'call_started': {
        const dir = d.direction === 'outbound' ? 'Outbound call to' : 'Handled inbound call from';
        return `${dir} ${this._formatPhone(d.phone_number)}`;
      }
      case 'call_ended': {
        const dur = d.duration_seconds ? ` (${this._formatDuration(d.duration_seconds)})` : '';
        const phone = d.phone_number ? ` with ${this._formatPhone(d.phone_number)}` : '';
        return `Completed call${phone}${dur}`;
      }
      case 'reservation_created': {
        const room = d.room_name ? ` for ${d.room_name}` : '';
        const conf = d.confirmation_number ? ` #${d.confirmation_number}` : '';
        return `Created reservation${conf}${room}`;
      }
      case 'payment_link_sent': {
        const order = d.order_id ? ` for order #${d.order_id}` : '';
        return `Sent payment link${order}`;
      }
      case 'tool_call_completed':
        return `Executed ${d.tool_name || 'tool'} successfully`;
      case 'tool_call_failed':
        return `Tool ${d.tool_name || 'unknown'} failed`;
      default:
        return evt.eventType.replace(/_/g, ' ');
    }
  }

  _viewAll() {
    this.dispatchEvent(new CustomEvent('switch-tab', {
      detail: 'activity',
      bubbles: true,
      composed: true,
    }));
  }

  _renderSkeleton() {
    return html`
      <div class="metrics-row">
        ${[1, 2, 3, 4].map(() => html`
          <div class="metric-card">
            <div class="skeleton-line" style="width:60%;height:12px;margin-bottom:10px;"></div>
            <div class="skeleton-line" style="width:40%;height:24px;margin-bottom:8px;"></div>
            <div class="skeleton-line short" style="height:10px;margin-bottom:0;"></div>
          </div>
        `)}
      </div>
      <div class="recent-calls-section">
        <div class="section-header">
          <div class="skeleton-line" style="width:120px;height:16px;margin-bottom:0;"></div>
        </div>
        ${[1, 2, 3, 4, 5].map(() => html`
          <div class="call-list-item">
            <div class="skeleton-line" style="width:32px;height:32px;border-radius:var(--radius-md);flex-shrink:0;margin-bottom:0;"></div>
            <div style="flex:1;display:flex;flex-direction:column;gap:4px;">
              <div class="skeleton-line" style="width:55%;height:14px;margin-bottom:0;"></div>
              <div class="skeleton-line" style="width:35%;height:10px;margin-bottom:0;"></div>
            </div>
            <div class="skeleton-line" style="width:40px;height:12px;margin-bottom:0;"></div>
          </div>
        `)}
      </div>
      <div class="recent-calls-section">
        <div class="section-header">
          <div class="skeleton-line" style="width:140px;height:16px;margin-bottom:0;"></div>
        </div>
        <div style="padding-left:var(--space-6);position:relative;">
          ${[1, 2, 3, 4].map(() => html`
            <div style="padding-bottom:var(--space-4);padding-left:var(--space-4);">
              <div class="skeleton-line" style="width:70%;height:14px;margin-bottom:4px;"></div>
              <div class="skeleton-line" style="width:30%;height:10px;margin-bottom:0;"></div>
            </div>
          `)}
        </div>
      </div>
    `;
  }

  render() {
    if (!this.agent) return html``;

    if (this._loading) {
      return html`
        ${iconStyles}
        ${this._renderSkeleton()}
      `;
    }

    const s = this._stats;

    return html`
      ${iconStyles}
      <div class="metrics-row">
        <div class="metric-card">
          <div class="metric-label">Total Calls</div>
          <div class="metric-value">${s.totalCalls.toLocaleString()}</div>
        </div>
        <div class="metric-card">
          <div class="metric-label">Completed</div>
          <div class="metric-value">${s.completedPct}%</div>
        </div>
        <div class="metric-card">
          <div class="metric-label">Avg Duration</div>
          <div class="metric-value">${this._formatDuration(s.avgDuration)}</div>
        </div>
        <div class="metric-card">
          <div class="metric-label">Recent</div>
          <div class="metric-value">${this._recentCalls.length}</div>
        </div>
      </div>

      <div class="recent-calls-section">
        <div class="section-header">
          <span class="section-title">Recent Calls</span>
          ${this._recentCalls.length > 0 ? html`
            <button class="btn btn-ghost btn-sm" @click=${this._viewAll}>View All</button>
          ` : ''}
        </div>
        ${this._recentCalls.length > 0 ? this._recentCalls.map(call => html`
          <div class="call-list-item">
            <div class="call-icon-wrap ${call.status === 'completed' ? 'success' : 'missed'}">
              <i class="fa-sharp fa-light ${call.status === 'completed' ? 'fa-phone' : 'fa-phone-missed'}"></i>
            </div>
            <div class="call-info">
              <div class="call-number">${this._formatPhone(call.phone)}</div>
              <div class="call-time">${this._formatDate(call.startedAt)}</div>
            </div>
            <div class="call-duration">${this._formatDuration(call.duration)}</div>
          </div>
        `) : html`
          <div class="empty-state" style="padding: var(--space-8);">
            <div class="empty-state-icon"><i class="fa-sharp fa-light fa-phone-slash"></i></div>
            <div class="empty-state-title">No recent calls</div>
            <div class="empty-state-description">This agent has not handled any calls yet.</div>
          </div>
        `}
      </div>

      <div class="recent-calls-section">
        <div class="section-header">
          <span class="section-title">Activity Timeline</span>
        </div>
        ${this._timelineEvents.length > 0 ? html`
          <div class="timeline">
            ${this._timelineEvents.map(evt => html`
              <div class="timeline-item">
                <div class="timeline-content">
                  <i class="fa-sharp fa-light ${this._eventIcon(evt.eventType)}" style="margin-right:var(--space-1);color:var(--color-text-tertiary);font-size:var(--text-xs);"></i>
                  ${this._eventText(evt)}
                </div>
                <div class="timeline-time">${agentService.relativeTime(evt.createdAt)}</div>
              </div>
            `)}
          </div>
        ` : html`
          <div class="empty-state" style="padding: var(--space-6);">
            <div class="empty-state-icon" style="font-size:var(--text-2xl);"><i class="fa-sharp fa-light fa-timeline"></i></div>
            <div class="empty-state-title" style="font-size:var(--text-sm);">No activity yet</div>
          </div>
        `}
      </div>
    `;
  }
}

customElements.define('agent-overview-tab', AgentOverviewTab);
